import { useState, useMemo } from 'react';
import { Plus, X, ChevronDown, ChevronUp, Sparkles, Wand2, Save, Search } from 'lucide-react';
import type { DeliverableItem, PersonaConfigData, PersonaTemplate, WorkflowStep } from './personaConfig';
import { ensureConfig } from './personaConfig';

// ── Props ──
interface PersonaEditorProps {
  value: PersonaConfigData;
  onChange: (value: PersonaConfigData) => void;
  onSave?: () => void;
  saving?: boolean;
  templates?: PersonaTemplate[];
  onApplyTemplate?: (templateId: string) => void;
}

// ── Section renderer ──
const Section = ({
  title,
  sectionKey,
  expandedSections,
  toggleSection,
  children,
}: {
  title: string;
  sectionKey: string;
  expandedSections: Set<string>;
  toggleSection: (key: string) => void;
  children: React.ReactNode;
}) => {
  const expanded = expandedSections.has(sectionKey);
  return (
    <div className="card mb-4">
      <button
        className="flex items-center justify-between w-full"
        onClick={() => toggleSection(sectionKey)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
      >
        <h3 style={{ margin: 0 }}>{title}</h3>
        {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {expanded && <div style={{ marginTop: 12 }}>{children}</div>}
    </div>
  );
};

const StringListEditor = ({
  label,
  items,
  placeholder,
  onAdd,
  onRemove,
  onUpdate,
}: {
  label: string;
  items: string[];
  placeholder: string;
  onAdd: () => void;
  onRemove: (i: number) => void;
  onUpdate: (i: number, v: string) => void;
}) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <label className="text-sm text-muted">{label}</label>
      <button className="secondary" onClick={onAdd}><Plus size={14} /> 添加</button>
    </div>
    {items.length === 0 ? (
      <p className="text-muted">暂无条目</p>
    ) : (
      <div className="flex flex-col gap-2">
        {items.map((item, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input value={item} onChange={e => onUpdate(i, e.target.value)} placeholder={placeholder} />
            <button className="secondary" onClick={() => onRemove(i)} style={{ padding: '8px 12px' }}>
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default function PersonaEditor({
  value,
  onChange,
  onSave,
  saving,
  templates,
  onApplyTemplate,
}: PersonaEditorProps) {
  const config = ensureConfig(value);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['basic']));
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateCategory, setTemplateCategory] = useState('');

  const templateCategories = useMemo(
    () => [...new Set((templates || []).map((t) => t.category).filter(Boolean))] as string[],
    [templates]
  );

  const filteredTemplates = useMemo(() => {
    const term = templateSearch.toLowerCase();
    return (templates || []).filter((t) => {
      const matchesSearch =
        !term ||
        t.name.toLowerCase().includes(term) ||
        (t.description || '').toLowerCase().includes(term) ||
        (t.category || '').toLowerCase().includes(term) ||
        (t.tags || []).some((tag) => tag.toLowerCase().includes(term));
      const matchesCategory = !templateCategory || t.category === templateCategory;
      return matchesSearch && matchesCategory;
    });
  }, [templates, templateSearch, templateCategory]);

  const toggleSection = (key: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const updateConfig = (patch: Partial<PersonaConfigData>) => {
    onChange({ ...config, ...patch });
  };

  const updateNested = <K extends keyof PersonaConfigData>(
    key: K,
    patch: Partial<NonNullable<PersonaConfigData[K]>>
  ) => {
    onChange({
      ...config,
      [key]: { ...(config[key] as Record<string, unknown>), ...patch },
    });
  };

  // ── Array helpers ──
  const addItem = (key: 'boundaries' | 'rules' | 'example_phrases' | 'scope' | 'out_of_scope' | 'advanced_capabilities') => {
    if (key === 'example_phrases') {
      updateNested('communication', { example_phrases: [...(config.communication?.example_phrases || []), ''] });
    } else if (key === 'scope') {
      updateNested('mission', { scope: [...(config.mission?.scope || []), ''] });
    } else if (key === 'out_of_scope') {
      updateNested('mission', { out_of_scope: [...(config.mission?.out_of_scope || []), ''] });
    } else {
      updateConfig({ [key]: [...(config[key] as string[]), ''] });
    }
  };

  const removeItem = (key: 'boundaries' | 'rules' | 'example_phrases' | 'scope' | 'out_of_scope' | 'advanced_capabilities', index: number) => {
    if (key === 'example_phrases') {
      const arr = [...(config.communication?.example_phrases || [])];
      arr.splice(index, 1);
      updateNested('communication', { example_phrases: arr });
    } else if (key === 'scope') {
      const arr = [...(config.mission?.scope || [])];
      arr.splice(index, 1);
      updateNested('mission', { scope: arr });
    } else if (key === 'out_of_scope') {
      const arr = [...(config.mission?.out_of_scope || [])];
      arr.splice(index, 1);
      updateNested('mission', { out_of_scope: arr });
    } else {
      const arr = [...(config[key] as string[])];
      arr.splice(index, 1);
      updateConfig({ [key]: arr });
    }
  };

  const updateItem = (key: 'boundaries' | 'rules' | 'example_phrases' | 'scope' | 'out_of_scope' | 'advanced_capabilities', index: number, value: string) => {
    if (key === 'example_phrases') {
      const arr = [...(config.communication?.example_phrases || [])];
      arr[index] = value;
      updateNested('communication', { example_phrases: arr });
    } else if (key === 'scope') {
      const arr = [...(config.mission?.scope || [])];
      arr[index] = value;
      updateNested('mission', { scope: arr });
    } else if (key === 'out_of_scope') {
      const arr = [...(config.mission?.out_of_scope || [])];
      arr[index] = value;
      updateNested('mission', { out_of_scope: arr });
    } else {
      const arr = [...(config[key] as string[])];
      arr[index] = value;
      updateConfig({ [key]: arr });
    }
  };

  const addDeliverable = () => {
    updateConfig({
      deliverables: [...config.deliverables, { name: '', template: '', format: 'markdown' }],
    });
  };

  const removeDeliverable = (index: number) => {
    const arr = [...config.deliverables];
    arr.splice(index, 1);
    updateConfig({ deliverables: arr });
  };

  const updateDeliverable = (index: number, field: keyof DeliverableItem, value: string) => {
    const arr = [...config.deliverables];
    arr[index] = { ...arr[index], [field]: value };
    updateConfig({ deliverables: arr });
  };

  const addWorkflowStep = () => {
    const nextStep = config.workflow.length + 1;
    updateConfig({
      workflow: [...config.workflow, { step: nextStep, name: '', action: '' }],
    });
  };

  const removeWorkflowStep = (index: number) => {
    const arr = [...config.workflow];
    arr.splice(index, 1);
    arr.forEach((s, i) => { s.step = i + 1; });
    updateConfig({ workflow: arr });
  };

  const updateWorkflowStep = (index: number, field: keyof WorkflowStep, value: string | number) => {
    const arr = [...config.workflow];
    arr[index] = { ...arr[index], [field]: value };
    updateConfig({ workflow: arr });
  };

  return (
    <div>
      {/* ── Template Selector ── */}
      {templates && templates.length > 0 && onApplyTemplate && (
        <div className="card mb-4" style={{ background: 'var(--bg-tertiary)' }}>
          <h3><Sparkles size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />从模板快速配置</h3>

          <div className="flex gap-2 mt-3" style={{ flexWrap: 'wrap' }}>
            <div style={{ flex: 2, minWidth: 200, position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--text-muted)' }} />
              <input
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                placeholder="搜索模板名称、描述或分类..."
                style={{ paddingLeft: 32, width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <select
              value={templateCategory}
              onChange={(e) => setTemplateCategory(e.target.value)}
              style={{ flex: 1, minWidth: 140 }}
            >
              <option value="">全部分类</option>
              {templateCategories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2 mt-3" style={{ flexWrap: 'wrap' }}>
            <select
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              style={{ flex: 1, minWidth: 240 }}
              size={Math.min(6, Math.max(3, filteredTemplates.length))}
            >
              <option value="">
                {filteredTemplates.length > 0
                  ? `-- 共 ${filteredTemplates.length} 个模板，请选择 --`
                  : '-- 无匹配模板 --'}
              </option>
              {filteredTemplates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.is_builtin ? '[预置]' : '[自建]'} {t.name} {t.category ? `（${t.category}）` : ''}
                </option>
              ))}
            </select>
            <button
              className="primary"
              onClick={() => onApplyTemplate(selectedTemplateId)}
              disabled={!selectedTemplateId}
            >
              <Wand2 size={14} /> 应用模板
            </button>
          </div>

          {selectedTemplateId && (
            <div className="text-muted mt-2" style={{ fontSize: 13, lineHeight: 1.5 }}>
              <strong>已选：</strong>
              {templates.find((t) => t.id === selectedTemplateId)?.name}
              <br />
              {templates.find((t) => t.id === selectedTemplateId)?.description}
            </div>
          )}
        </div>
      )}

      {/* ── Basic Info ── */}
      <Section expandedSections={expandedSections} toggleSection={toggleSection} title="基本信息" sectionKey="basic">
        <div className="flex flex-col gap-3">
          <div>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>角色定位 (role)</label>
            <input
              value={config.role}
              onChange={e => updateConfig({ role: e.target.value })}
              placeholder="如：专业客服、技术顾问、知识导师"
            />
          </div>
          <div>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>语气风格 (tone)</label>
            <input
              value={config.tone}
              onChange={e => updateConfig({ tone: e.target.value })}
              placeholder="如：亲切专业、严谨清晰、耐心鼓励"
            />
          </div>
          <div>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>语言习惯 (language)</label>
            <input
              value={config.language}
              onChange={e => updateConfig({ language: e.target.value })}
              placeholder="如：中文、英文、简洁长句"
            />
          </div>
        </div>
      </Section>

      {/* ── Identity ── */}
      <Section expandedSections={expandedSections} toggleSection={toggleSection} title="身份设定 (Identity)" sectionKey="identity">
        <div className="flex flex-col gap-3">
          <div>
            <label className="text-sm text-muted">角色</label>
            <input
              value={config.identity?.role || ''}
              onChange={e => updateNested('identity', { role: e.target.value })}
              placeholder="Agent 的具体角色名称"
            />
          </div>
          <div>
            <label className="text-sm text-muted">领域</label>
            <input
              value={config.identity?.domain || ''}
              onChange={e => updateNested('identity', { domain: e.target.value })}
              placeholder="所属专业领域"
            />
          </div>
          <div>
            <label className="text-sm text-muted">经验背景</label>
            <input
              value={config.identity?.experience || ''}
              onChange={e => updateNested('identity', { experience: e.target.value })}
              placeholder="经验描述，如：10年临床减重咨询经验"
            />
          </div>
        </div>
      </Section>

      {/* ── Mission ── */}
      <Section expandedSections={expandedSections} toggleSection={toggleSection} title="使命范围 (Mission)" sectionKey="mission">
        <div className="flex flex-col gap-3">
          <div>
            <label className="text-sm text-muted">核心使命</label>
            <input
              value={config.mission?.primary || ''}
              onChange={e => updateNested('mission', { primary: e.target.value })}
              placeholder="一句话描述核心使命"
            />
          </div>
          <StringListEditor
            label="职责范围 (scope)"
            items={config.mission?.scope || []}
            placeholder="如：产品咨询、订单查询"
            onAdd={() => addItem('scope')}
            onRemove={i => removeItem('scope', i)}
            onUpdate={(i, v) => updateItem('scope', i, v)}
          />
          <StringListEditor
            label="范围外事项 (out_of_scope)"
            items={config.mission?.out_of_scope || []}
            placeholder="如：医疗诊断、投资建议"
            onAdd={() => addItem('out_of_scope')}
            onRemove={i => removeItem('out_of_scope', i)}
            onUpdate={(i, v) => updateItem('out_of_scope', i, v)}
          />
        </div>
      </Section>

      {/* ── Rules & Boundaries ── */}
      <Section expandedSections={expandedSections} toggleSection={toggleSection} title="规则与边界 (Rules & Boundaries)" sectionKey="rules">
        <div className="flex flex-col gap-4">
          <StringListEditor
            label="行为规则 (rules)"
            items={config.rules}
            placeholder="如：首次响应不超过30秒"
            onAdd={() => addItem('rules')}
            onRemove={i => removeItem('rules', i)}
            onUpdate={(i, v) => updateItem('rules', i, v)}
          />
          <StringListEditor
            label="边界约束 (boundaries)"
            items={config.boundaries}
            placeholder="如：不提供医疗建议"
            onAdd={() => addItem('boundaries')}
            onRemove={i => removeItem('boundaries', i)}
            onUpdate={(i, v) => updateItem('boundaries', i, v)}
          />
        </div>
      </Section>

      {/* ── Deliverables ── */}
      <Section expandedSections={expandedSections} toggleSection={toggleSection} title="交付物 (Deliverables)" sectionKey="deliverables">
        <div className="flex flex-col gap-3">
          <button className="secondary" onClick={addDeliverable}><Plus size={14} /> 添加交付物</button>
          {config.deliverables.length === 0 && <p className="text-muted">暂无交付物定义</p>}
          {config.deliverables.map((d, i) => (
            <div key={i} className="card" style={{ background: 'var(--bg-tertiary)' }}>
              <div className="flex gap-2 items-center justify-between mb-2">
                <span className="text-sm text-muted">交付物 #{i + 1}</span>
                <button className="secondary" onClick={() => removeDeliverable(i)} style={{ padding: '4px 8px' }}>
                  <X size={12} />
                </button>
              </div>
              <div className="flex flex-col gap-2">
                <input
                  value={d.name}
                  onChange={e => updateDeliverable(i, 'name', e.target.value)}
                  placeholder="交付物名称"
                />
                <input
                  value={d.format}
                  onChange={e => updateDeliverable(i, 'format', e.target.value)}
                  placeholder="格式 (markdown / json / etc)"
                />
                <textarea
                  value={d.template}
                  onChange={e => updateDeliverable(i, 'template', e.target.value)}
                  placeholder="模板内容，可用 {变量} 占位"
                  rows={3}
                />
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Workflow ── */}
      <Section expandedSections={expandedSections} toggleSection={toggleSection} title="工作流 (Workflow)" sectionKey="workflow">
        <div className="flex flex-col gap-3">
          <button className="secondary" onClick={addWorkflowStep}><Plus size={14} /> 添加步骤</button>
          {config.workflow.length === 0 && <p className="text-muted">暂无工作流定义</p>}
          {config.workflow.map((w, i) => (
            <div key={i} className="flex gap-2 items-start">
              <span className="text-muted" style={{ minWidth: 32, paddingTop: 8 }}>{w.step}.</span>
              <div className="flex flex-col gap-2" style={{ flex: 1 }}>
                <input
                  value={w.name}
                  onChange={e => updateWorkflowStep(i, 'name', e.target.value)}
                  placeholder="步骤名称"
                />
                <input
                  value={w.action}
                  onChange={e => updateWorkflowStep(i, 'action', e.target.value)}
                  placeholder="具体操作"
                />
              </div>
              <button className="secondary" onClick={() => removeWorkflowStep(i)} style={{ padding: '8px 12px' }}>
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Communication ── */}
      <Section expandedSections={expandedSections} toggleSection={toggleSection} title="沟通风格 (Communication)" sectionKey="communication">
        <div className="flex flex-col gap-3">
          <div>
            <label className="text-sm text-muted">语气基调</label>
            <input
              value={config.communication?.tone || ''}
              onChange={e => updateNested('communication', { tone: e.target.value })}
              placeholder="如：亲切专业"
            />
          </div>
          <div>
            <label className="text-sm text-muted">表达风格</label>
            <input
              value={config.communication?.style || ''}
              onChange={e => updateNested('communication', { style: e.target.value })}
              placeholder="如：先共情后建议，使用鼓励性语言"
            />
          </div>
          <StringListEditor
            label="示例短语 (example_phrases)"
            items={config.communication?.example_phrases || []}
            placeholder="如：您的努力一定会有回报的！"
            onAdd={() => addItem('example_phrases')}
            onRemove={i => removeItem('example_phrases', i)}
            onUpdate={(i, v) => updateItem('example_phrases', i, v)}
          />
        </div>
      </Section>

      {/* ── Metrics ── */}
      <Section expandedSections={expandedSections} toggleSection={toggleSection} title="成功指标 (Metrics)" sectionKey="metrics">
        <div className="flex flex-col gap-3">
          <div>
            <label className="text-sm text-muted">用户满意度目标 (1-5)</label>
            <input
              type="number"
              step={0.1}
              min={1}
              max={5}
              value={config.metrics?.user_satisfaction_target ?? ''}
              onChange={e => updateNested('metrics', { user_satisfaction_target: e.target.value ? parseFloat(e.target.value) : undefined })}
            />
          </div>
          <div>
            <label className="text-sm text-muted">合规率目标 (0-1)</label>
            <input
              type="number"
              step={0.01}
              min={0}
              max={1}
              value={config.metrics?.compliance_rate_target ?? ''}
              onChange={e => updateNested('metrics', { compliance_rate_target: e.target.value ? parseFloat(e.target.value) : undefined })}
            />
          </div>
          <div>
            <label className="text-sm text-muted">安全事件目标 (0-N)</label>
            <input
              type="number"
              min={0}
              value={config.metrics?.safety_incidents_target ?? ''}
              onChange={e => updateNested('metrics', { safety_incidents_target: e.target.value ? parseInt(e.target.value) : undefined })}
            />
          </div>
        </div>
      </Section>

      {/* ── Advanced Capabilities ── */}
      <Section expandedSections={expandedSections} toggleSection={toggleSection} title="高级能力 (Advanced Capabilities)" sectionKey="advanced">
        <StringListEditor
          label="特殊能力描述"
          items={config.advanced_capabilities}
          placeholder="如：能识别用户情绪并调整回应方式"
          onAdd={() => addItem('advanced_capabilities')}
          onRemove={i => removeItem('advanced_capabilities', i)}
          onUpdate={(i, v) => updateItem('advanced_capabilities', i, v)}
        />
      </Section>

      {/* ── Save Button ── */}
      {onSave && (
        <button className="primary" onClick={onSave} style={{ width: '100%' }} disabled={saving}>
          <Save size={14} /> {saving ? '保存中...' : '保存配置'}
        </button>
      )}
    </div>
  );
}
